// Responsive design utilities and breakpoints

export const breakpoints = {
  xs: '375px',   // Small phones
  sm: '640px',   // Large phones
  md: '768px',   // Tablets
  lg: '1024px',  // Small desktops
  xl: '1200px',  // Large desktops
  xxl: '1536px'  // Extra large screens
};

export const spacing = {
  xs: '4px',
  sm: '8px', 
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px'
};

export const touchTargets = {
  minimum: '44px',     // WCAG minimum
  comfortable: '48px', // Comfortable touch
  large: '56px'        // Large touch targets
};

// Media query helper
export const mediaQuery = (breakpoint) => `@media (min-width: ${breakpoints[breakpoint]})`;

// Check if device is mobile
export const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < parseInt(breakpoints.md);
};

// Touch-friendly button styles
export const getButtonStyle = (variant = 'primary', size = 'medium', isMobileDevice = false) => {
  const baseStyle = {
    fontFamily: 'inherit',
    fontWeight: 600,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
  };

  // Size variants - mobile-first approach
  const sizes = {
    small: {
      fontSize: isMobileDevice ? '14px' : '12px',
      padding: isMobileDevice ? '12px 16px' : '8px 12px',
      minHeight: isMobileDevice ? touchTargets.minimum : '32px',
      minWidth: isMobileDevice ? touchTargets.comfortable : '60px',
    },
    medium: {
      fontSize: isMobileDevice ? '16px' : '14px',
      padding: isMobileDevice ? '14px 20px' : '10px 16px',
      minHeight: isMobileDevice ? touchTargets.comfortable : '36px',
      minWidth: isMobileDevice ? touchTargets.large : '72px',
    },
    large: {
      fontSize: isMobileDevice ? '18px' : '16px',
      padding: isMobileDevice ? '16px 24px' : '12px 20px',
      minHeight: isMobileDevice ? touchTargets.large : '44px',
      minWidth: isMobileDevice ? '64px' : '90px',
    }
  };

  return {
    ...baseStyle,
    ...sizes[size]
  };
};

// Responsive grid utilities
export const getGridStyle = (isMobileDevice = false) => {
  if (isMobileDevice) {
    return {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.md,
      padding: spacing.md
    };
  }
  
  return {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    gap: spacing.lg,
    padding: `${spacing.md} ${spacing.lg}`
  };
};

// Typography scale for mobile
export const getTypographyStyle = (level, isMobileDevice = false) => {
  const mobileScale = {
    h1: { fontSize: '28px', lineHeight: '1.2', fontWeight: 700 },
    h2: { fontSize: '24px', lineHeight: '1.25', fontWeight: 600 },
    h3: { fontSize: '20px', lineHeight: '1.3', fontWeight: 600 },
    body: { fontSize: '16px', lineHeight: '1.5', fontWeight: 400 },
    small: { fontSize: '14px', lineHeight: '1.4', fontWeight: 400 },
    caption: { fontSize: '12px', lineHeight: '1.3', fontWeight: 400 }
  };

  const desktopScale = {
    h1: { fontSize: '32px', lineHeight: '1.2', fontWeight: 700 },
    h2: { fontSize: '24px', lineHeight: '1.25', fontWeight: 600 },
    h3: { fontSize: '18px', lineHeight: '1.3', fontWeight: 600 },
    body: { fontSize: '14px', lineHeight: '1.5', fontWeight: 400 },
    small: { fontSize: '12px', lineHeight: '1.4', fontWeight: 400 },
    caption: { fontSize: '11px', lineHeight: '1.3', fontWeight: 400 }
  };

  return isMobileDevice ? mobileScale[level] : desktopScale[level];
};